import {
  addDays, COST_AMOUNT_MAX, COST_FIRST_DAY, COST_KINDS, COST_NOTE_MAX, COST_REASON_MAX, LIFE_YEARS_MAX, LIFE_YEARS_MIN,
  type AssetCostRecordV1, type AssetCostReport, type AssetFinancialSetV1, type CostKind, type CostLine,
} from '@gk/contracts'
import type { StockMovementRow } from './catalog.ts'
import { dayText, isDay } from './catalogueUi.ts'
import { sizeText, typeText } from './documents.ts'
import type { WorkOrderRow } from './safety.ts'

/** M15-E1, the machine's costs (docs/M15.md E1): the screens' pure rules, with their tests (data/finance.test.ts). Everything a cost screen shows is read ONLINE
 *  through the audited `api.report_asset_costs`, behind the second factor, and kept in the page's state only (rule 5: kept on no device); the three commands
 *  are Y* (`useSensitive().command`). Nothing here prices an hour or a litre, and nothing here advises: the report prints what was recorded, and says that
 *  repairing or replacing is a person's judgement (rule 6). */
export { COST_KINDS, COST_NOTE_MAX, COST_REASON_MAX, LIFE_YEARS_MAX, LIFE_YEARS_MIN }
export type { AssetCostReport, CostKind, CostLine }

type T = (k: string, o?: Record<string, unknown>) => string
/** The words of each kind (literal keys, so i18nKeys.test.ts holds each one to the catalogue). */
export function costKindText(t: T, k: string): string {
  switch (k) {
    case 'parts': return t('costs.kind.parts')
    case 'external_repair': return t('costs.kind.external_repair')
    case 'service': return t('costs.kind.service')
    case 'fuel': return t('costs.kind.fuel')
    case 'other': return t('costs.kind.other')
    default: return k
  }
}

/** An amount for people, always with its two decimals and its currency: "1 250,50 €" (a cost is money: never "1 250,5"). */
export const fmtMoney = (n: number, lang: string, currency = 'EUR'): string =>
  new Intl.NumberFormat(lang === 'en' ? 'en-GB' : 'fr-FR', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
/** Hours and litres for people: at most the decimals given, no trailing zero (the app's `fmtNum` rule). */
export const fmtQty = (n: number, lang: string, max = 2): string => new Intl.NumberFormat(lang === 'en' ? 'en-GB' : 'fr-FR', { maximumFractionDigits: max }).format(n)
/** "oct. 2026" for a 'YYYY-MM' month. */
export const monthText = (month: string, lang: string): string => {
  const [y, m] = month.split('-').map(Number)
  return new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'fr-FR', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y!, m! - 1, 1)))
}
export { addDays, dayText }

// ---------------------------------------------------------------------------------------------------- an amount as typed
export type AmountProblem = 'empty' | 'invalid' | 'decimals' | 'range'
/** What someone types into an amount field: "1 250,50", "1250.5", "1,250.50" (a thousands comma), "1.250,50" (a thousands dot), "1.250.000". A third decimal
 *  is REFUSED, never rounded (the server refuses it too: PAYLOAD_INVALID), and so is anything over the column's bound. One mark once followed by three digits
 *  ("1,005", "1.250": a thousand, or a third decimal?) is never guessed: it is read as the decimal mark and refused, and the hint says to write thousands with a space. */
export function parseAmount(raw: string): { value: number } | { problem: AmountProblem } {
  const s = raw.replace(/[\s  €]/g, '')
  if (!s) return { problem: 'empty' }
  if (!/^[\d.,]+$/.test(s) || !/\d/.test(s)) return { problem: 'invalid' }
  const commas = (s.match(/,/g) ?? []).length, dots = (s.match(/\./g) ?? []).length
  let t: string
  if (commas > 0 && dots > 0) {
    // both marks: the last one is the decimal mark, the other groups the thousands ("1.250,50", "1,250.50")
    const dec = s.lastIndexOf(',') > s.lastIndexOf('.') ? ',' : '.'
    const parts = s.split(dec)
    const grouped = dec === ',' ? /^\d{1,3}(\.\d{3})+$/ : /^\d{1,3}(,\d{3})+$/
    if (parts.length !== 2 || !grouped.test(parts[0]!)) return { problem: 'invalid' }
    t = `${parts[0]!.replace(/[.,]/g, '')}.${parts[1]}`
  } else if (commas + dots > 1) {
    // one mark several times: thousands groups only ("1.250.000")
    if (!/^\d{1,3}([.,]\d{3})+$/.test(s)) return { problem: 'invalid' }
    t = s.replace(/[.,]/g, '')
  } else t = s.replace(',', '.')                // one mark once: the decimal mark ("1,005" is then a third decimal: refused, never guessed as a thousand)
  if (!/^\d+(\.\d+)?$/.test(t)) return { problem: 'invalid' }
  if (/\.\d{3,}$/.test(t)) return { problem: 'decimals' }
  const n = Number(t)
  if (!Number.isFinite(n) || n > COST_AMOUNT_MAX) return { problem: 'range' }
  return { value: n }
}
/** An amount as a form shows it again ("42000,5" → "42 000,50" is the display's; the field keeps a plain "42000.50" written the French way). */
export const amountField = (n: number | null | undefined, lang: string): string =>
  n === null || n === undefined ? '' : (lang === 'en' ? n.toFixed(2) : n.toFixed(2).replace('.', ','))

// ---------------------------------------------------------------------------------------------------- days and periods
export const PERIODS = ['year', 'last_year', 'last12', 'all'] as const
export type Period = (typeof PERIODS)[number]
export function periodText(t: T, p: Period, today: string): string {
  switch (p) {
    case 'year': return t('costs.period.year', { year: today.slice(0, 4) })
    case 'last_year': return t('costs.period.lastYear', { year: String(Number(today.slice(0, 4)) - 1) })
    case 'last12': return t('costs.period.last12')
    case 'all': return t('costs.period.all')
  }
}
/** The local days a period covers, on the site's today: this calendar year so far, last year, the last twelve months (the same day a year ago excluded),
 *  or the machine's whole life (from its acquisition day, else the 1st of January of its year, else the first day a cost may carry). */
export function periodRange(p: Period, today: string, asset?: { acquired_on?: string | null; year?: number | null }): { from: string; to: string } {
  const y = Number(today.slice(0, 4))
  switch (p) {
    case 'year': return { from: `${y}-01-01`, to: today }
    case 'last_year': return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` }
    case 'last12': return { from: addDays(`${y - 1}${today.slice(4)}`.replace(/-02-29$/, '-02-28'), 1), to: today }
    case 'all': {
      const since = asset?.acquired_on ?? (asset?.year ? `${asset.year}-01-01` : COST_FIRST_DAY)
      return { from: since < today ? since : today, to: today }
    }
  }
}

/** A machine's age on a day: whole years and months from its acquisition day, or (no day known) whole years from its year, said as "about". null when the
 *  register holds neither. The arithmetic of the calendar, nothing more. */
export function ageOf(asset: { acquired_on?: string | null; year?: number | null }, today: string): { years: number; months: number; approx: boolean } | null {
  if (asset.acquired_on) {
    const [ay, am, ad] = asset.acquired_on.split('-').map(Number) as [number, number, number]
    const [ty, tm, td] = today.split('-').map(Number) as [number, number, number]
    let months = (ty - ay) * 12 + (tm - am) - (td < ad ? 1 : 0)
    if (months < 0) months = 0
    return { years: Math.floor(months / 12), months: months % 12, approx: false }
  }
  if (asset.year) return { years: Math.max(0, Number(today.slice(0, 4)) - asset.year), months: 0, approx: true }
  return null
}

// ---------------------------------------------------------------------------------------------------- the cost form
export interface CostForm { day: string; kind: CostKind | ''; amount: string; note: string; workOrderId: string; movementId: string }
export const blankCostForm = (today: string): CostForm => ({ day: today, kind: '', amount: '', note: '', workOrderId: '', movementId: '' })
export type CostProblem = 'kind' | 'day' | 'dayFuture' | 'dayOld' | AmountProblem | 'noteLong'
/** The form's checks, in the order the server judges them (ASSET_COST_DATE_INVALID, PAYLOAD_INVALID); the first one found is said next to the button. */
export function costProblem(f: CostForm, today: string): CostProblem | null {
  if (!f.kind) return 'kind'
  if (!isDay(f.day)) return 'day'
  if (f.day > addDays(today, 1)) return 'dayFuture'
  if (f.day < COST_FIRST_DAY) return 'dayOld'
  const a = parseAmount(f.amount)
  if ('problem' in a) return a.problem
  if ([...f.note.trim()].length > COST_NOTE_MAX) return 'noteLong'
  return null
}
export function costProblemText(t: T, p: CostProblem): string {
  switch (p) {
    case 'kind': return t('costs.form.missing.kind')
    case 'day': return t('costs.form.missing.day')
    case 'dayFuture': return t('costs.form.missing.dayFuture')
    case 'dayOld': return t('costs.form.missing.dayOld')
    case 'empty': return t('costs.form.missing.amount')
    case 'invalid': return t('costs.form.missing.amountInvalid')
    case 'decimals': return t('costs.form.missing.amountDecimals')
    case 'range': return t('costs.form.missing.amountRange')
    case 'noteLong': return t('costs.form.missing.noteLong', { max: COST_NOTE_MAX })
  }
}
/** The payload of `asset_cost.record@1` from a form that passed `costProblem`: the note trimmed (or none), the links only when chosen (a fill-up only for fuel). */
export function buildCostPayload(costId: string, assetId: string, f: CostForm): AssetCostRecordV1 {
  const a = parseAmount(f.amount)
  if ('problem' in a || !f.kind) throw new Error('costProblem first')
  const note = f.note.trim()
  return {
    cost_id: costId, asset_id: assetId, incurred_on: f.day, kind: f.kind, amount: a.value,
    ...(f.workOrderId ? { work_order_id: f.workOrderId } : {}), ...(f.kind === 'fuel' && f.movementId ? { movement_id: f.movementId } : {}), ...(note ? { note } : {}),
  }
}

// ---------------------------------------------------------------------------------------------------- the financials form
export interface FinancialForm { purchase: string; life: string; residual: string }
export const financialFormOf = (f: AssetCostReport['financial'], lang: string): FinancialForm =>
  ({ purchase: amountField(f?.purchase_cost, lang), life: f?.expected_life_years ? String(f.expected_life_years) : '', residual: amountField(f?.residual_value, lang) })
export type FinancialProblem = 'purchase' | 'residual' | 'life' | 'nothing'
const optionalAmount = (raw: string): { value: number | null } | { problem: AmountProblem } => (raw.trim() ? parseAmount(raw) : { value: null })
/** The payload of `asset_financial.set@1`: only what CHANGED from what was read (an absent key keeps, a cleared field sends null), or a problem. */
export function buildFinancialPayload(assetId: string, f: FinancialForm, stored: AssetCostReport['financial']): { payload: AssetFinancialSetV1 } | { problem: FinancialProblem } {
  const purchase = optionalAmount(f.purchase); if ('problem' in purchase) return { problem: 'purchase' }
  const residual = optionalAmount(f.residual); if ('problem' in residual) return { problem: 'residual' }
  const lifeRaw = f.life.trim()
  if (lifeRaw && !/^\d+$/.test(lifeRaw)) return { problem: 'life' }
  const life = lifeRaw ? Number(lifeRaw) : null
  if (life !== null && (life < LIFE_YEARS_MIN || life > LIFE_YEARS_MAX)) return { problem: 'life' }
  const out: Record<string, number | null> = {}
  if (purchase.value !== (stored?.purchase_cost ?? null)) out.purchase_cost = purchase.value
  if (life !== (stored?.expected_life_years ?? null)) out.expected_life_years = life
  if (residual.value !== (stored?.residual_value ?? null)) out.residual_value = residual.value
  if (Object.keys(out).length === 0) return { problem: 'nothing' }
  return { payload: { asset_id: assetId, ...out } as AssetFinancialSetV1 }
}
export function financialProblemText(t: T, p: FinancialProblem): string {
  switch (p) {
    case 'purchase': return t('costs.financial.missing.purchase')
    case 'residual': return t('costs.financial.missing.residual')
    case 'life': return t('costs.financial.missing.life', { min: LIFE_YEARS_MIN, max: LIFE_YEARS_MAX })
    case 'nothing': return t('costs.financial.missing.nothing')
  }
}

/** A void's reason: required (ASSET_COST_REASON_REQUIRED), at most 300 code points. */
export function voidProblem(reason: string): 'empty' | 'long' | null {
  const r = reason.trim()
  if (!r) return 'empty'
  return [...r].length > COST_REASON_MAX ? 'long' : null
}

// ---------------------------------------------------------------------------------------------------- what the report shows
export interface MonthRow { month: string; amount: number; costs: number; byKind: Partial<Record<CostKind, number>>; litres: number | null; fills: number; hours: number | null }
/** One row per month the report holds anything in (a cost, a fill-up, an hour), newest first: the cash costs by kind beside the litres and the hours, never
 *  a price computed from them. A month with no litres or no hours says so (null), never 0. */
export function monthRows(r: Pick<AssetCostReport, 'by_month' | 'litres' | 'hours'>): MonthRow[] {
  const rows = new Map<string, MonthRow>()
  const row = (month: string) => {
    let x = rows.get(month)
    if (!x) { x = { month, amount: 0, costs: 0, byKind: {}, litres: null, fills: 0, hours: null }; rows.set(month, x) }
    return x
  }
  for (const m of r.by_month) { const x = row(m.month); x.amount = round2(x.amount + Number(m.amount)); x.costs += m.count; x.byKind[m.kind] = round2((x.byKind[m.kind] ?? 0) + Number(m.amount)) }
  for (const m of r.litres.by_month) { const x = row(m.month); x.fills += m.fills; if (m.litres !== null) x.litres = Number(m.litres) }
  for (const m of r.hours.by_month) { const x = row(m.month); x.hours = Number(m.hours) }
  return [...rows.values()].sort((a, b) => (a.month < b.month ? 1 : -1))
}
const round2 = (n: number) => Math.round(n * 100) / 100
/** A counter's run over the period (the readings at both ends, as they were then): never a substitute for the hours allocated (I1). */
export const meterInPeriod = (m: AssetCostReport['meters'][number]): number => round2(Number(m.at_to) - Number(m.at_from))
/** The costs of the period as the list shows them: the recorded ones first by day (newest first), each voided one after the others of its day. */
export const costRows = (costs: readonly CostLine[]): CostLine[] =>
  [...costs].sort((a, b) => (a.incurred_on !== b.incurred_on ? (a.incurred_on < b.incurred_on ? 1 : -1) : Number(!!a.voided_at) - Number(!!b.voided_at) || (a.created_at < b.created_at ? 1 : -1)))

// ---------------------------------------------------------------------------------------------------- what a cost may name (from the device's copy)
/** The machine's workshop orders a cost may name, the open ones first then the newest (the server accepts only this machine's: ASSET_COST_LINK_INVALID). */
export function orderOptions(orders: readonly WorkOrderRow[], assetId: string): WorkOrderRow[] {
  const open = (o: WorkOrderRow) => o.state !== 'closed' && o.state !== 'cancelled'
  return orders.filter((o) => o.asset_id === assetId).sort((a, b) => Number(open(b)) - Number(open(a)) || (a.opened_at < b.opened_at ? 1 : -1)).slice(0, 20)
}
/** The machine's own fill-ups a fuel invoice may name, newest first, a reversed one left out (as the report leaves it out of the litres). */
export function fillUpOptions(movements: readonly StockMovementRow[], assetId: string): StockMovementRow[] {
  const reversed = new Set(movements.filter((m) => m.reason === 'reversal' && m.reversal_of).map((m) => m.reversal_of!))
  return movements.filter((m) => m.reason === 'refuel' && m.asset_id === assetId && !reversed.has(m.id)).sort((a, b) => (a.occurred_at < b.occurred_at ? 1 : -1)).slice(0, 20)
}

/** A file of an invoice: "PDF · 2,4 Mo" (never previewed: handed to the phone's own viewer). */
// release b review (finding 15): ONE way of saying a file's size and type across release b — the vault's (data/documents.ts: the buckets' limits are binary, whole ko)
export const fileTypeText = (mime: string): string => typeText(mime)
export const fileSizeText = (bytes: number, lang: string): string => sizeText(bytes, lang)
/** The name a downloaded invoice gets: its own (the caption the device sent), else "facture-<day>.<ext>". */
export function invoiceFileName(f: { caption: string | null; mime: string }, cost: { incurred_on: string }): string {
  if (f.caption && /\.[a-z0-9]{2,4}$/i.test(f.caption)) return f.caption
  const ext = f.mime === 'application/pdf' ? 'pdf' : f.mime === 'image/png' ? 'png' : f.mime === 'image/webp' ? 'webp' : 'jpg'
  return `facture-${cost.incurred_on}.${ext}`
}

/** The invoices this device has registered on a cost and not yet sent (its outbox, read live): "1 facture en attente d'envoi" on the cost's line. Once the
 *  server has a register of a sensitive kind its row leaves the outbox (sync/transient.ts), and the report lists the file. */
const WAITING: ReadonlySet<string> = new Set(['queued', 'sending', 'transient', 'blocked'])
export function waitingInvoiceCounts(rows: ReadonlyArray<{ name: string; status: string; payload?: Record<string, unknown> | null }>): Map<string, number> {
  const out = new Map<string, number>()
  for (const r of rows) {
    if (r.name !== 'attachment.register' || !WAITING.has(r.status) || r.payload?.entity_type !== 'fleet.asset_cost') continue
    const id = String(r.payload.entity_id)
    out.set(id, (out.get(id) ?? 0) + 1)
  }
  return out
}

/** Réglages › Coûts' list: every asset of the register may carry costs (a machine, a vehicle, a cutting unit sent for grinding), those in service first, then
 *  the retired ones (their costs stay a record), each by tag; the page shows the first `PICK_MACHINES` and says how many more a search reaches. */
export const PICK_MACHINES = 12
export function costMachines<A extends { readable_tag: string; lifecycle?: string | null }>(assets: readonly A[]): A[] {
  const retired = (a: A) => (a.lifecycle && a.lifecycle !== 'active' ? 1 : 0)
  return [...assets].sort((a, b) => retired(a) - retired(b) || a.readable_tag.localeCompare(b.readable_tag, 'fr'))
}
